import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/auth";
import { NAV } from "@/nav";
import { LogOut, Save, ChevronLeft, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminGate = ({ children }) => {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (ready && !user) nav("/admin/login", { replace: true });
  }, [ready, user, nav]);
  if (!ready) return <div className="max-w-7xl mx-auto px-6 py-24 text-stone" data-testid="admin-loading">Checking session…</div>;
  if (!user) return null;
  return children;
};

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const { authHeader } = useAuth();

  useEffect(() => {
    axios.get(`${API}/admin/messages`, { headers: authHeader() }).then((r) => setMessages(r.data)).catch(() => {});
  }, [authHeader]);

  return (
    <section className="border-b border-line" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-start justify-between mb-12 gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-3">Editor</p>
            <h1 className="font-serif text-4xl lg:text-5xl text-ink">Signed in as {user?.email}</h1>
          </div>
          <button
            onClick={() => { logout(); nav("/admin/login"); }}
            data-testid="admin-logout"
            className="btn-outline-ink px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.24em] text-terracotta mb-4">Pages you can edit</p>
            <div className="border border-line divide-y divide-line">
              {NAV.flatMap((c) => c.items).map((item) => (
                <Link
                  key={item.slug}
                  to={`/admin/edit/${item.slug}`}
                  data-testid={`admin-edit-${item.slug}`}
                  className="flex items-center justify-between px-5 py-4 bg-paper hover:bg-cream transition-colors"
                >
                  <span className="text-ink">{item.label}</span>
                  <span className="text-xs text-stone">Edit →</span>
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-terracotta mb-4">Recent notes from readers</p>
            <div className="border border-line divide-y divide-line max-h-[500px] overflow-auto" data-testid="admin-messages">
              {messages.length === 0 && <p className="p-5 text-sm text-stone">No notes yet.</p>}
              {messages.map((m) => (
                <div key={m.id} className="p-5 bg-paper">
                  <div className="flex items-baseline justify-between gap-3 mb-1">
                    <p className="font-medium text-ink text-sm">{m.name} <span className="text-stone font-normal">· {m.role}</span></p>
                    <p className="text-xs text-stone">{new Date(m.created_at).toLocaleDateString()}</p>
                  </div>
                  <p className="text-xs text-stone mb-2">{m.email}</p>
                  <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const AdminEdit = () => {
  const { slug } = useParams();
  const { authHeader } = useAuth();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pages/${slug}`).then((r) => setPage(r.data)).finally(() => setLoading(false));
  }, [slug]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(
        `${API}/pages/${slug}`,
        { title: page.title, subtitle: page.subtitle, sections: page.sections },
        { headers: authHeader() }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !page) return <div className="max-w-5xl mx-auto px-6 py-16 text-stone">Loading…</div>;

  const updateSection = (i, key, value) => {
    const next = [...page.sections];
    next[i] = { ...next[i], [key]: value };
    setPage({ ...page, sections: next });
  };
  const addSection = () => setPage({ ...page, sections: [...page.sections, { heading: "New section", body: "" }] });
  const removeSection = (i) => setPage({ ...page, sections: page.sections.filter((_, idx) => idx !== i) });

  return (
    <section className="border-b border-line" data-testid={`admin-edit-page-${slug}`}>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
        <button onClick={() => nav("/admin")} className="text-sm text-stone hover:text-terracotta inline-flex items-center gap-1 mb-8" data-testid="admin-back">
          <ChevronLeft size={14} /> Back to dashboard
        </button>
        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-3">Editing · {slug}</p>
        <h1 className="font-serif text-3xl lg:text-4xl text-ink mb-8">Update this page</h1>

        <label className="text-xs uppercase tracking-[0.22em] text-stone">Title</label>
        <input
          data-testid="edit-title"
          className="w-full border border-line bg-paper px-4 py-3 mt-2 mb-6 font-serif text-2xl focus:outline-none focus:border-ink"
          value={page.title || ""}
          onChange={(e) => setPage({ ...page, title: e.target.value })}
        />

        <label className="text-xs uppercase tracking-[0.22em] text-stone">Subtitle</label>
        <textarea
          data-testid="edit-subtitle"
          rows={2}
          className="w-full border border-line bg-paper px-4 py-3 mt-2 mb-10 text-sm focus:outline-none focus:border-ink resize-none"
          value={page.subtitle || ""}
          onChange={(e) => setPage({ ...page, subtitle: e.target.value })}
        />

        <p className="text-xs uppercase tracking-[0.24em] text-terracotta mb-4">Sections</p>
        <div className="space-y-6">
          {(page.sections || []).map((s, i) => (
            <div key={i} className="border border-line p-5 bg-paper" data-testid={`edit-section-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-stone">Section {i + 1}</span>
                <button onClick={() => removeSection(i)} className="text-xs text-terracotta inline-flex items-center gap-1" data-testid={`remove-section-${i}`}>
                  <Trash2 size={12} /> Remove
                </button>
              </div>
              <input
                className="w-full border border-line bg-cream/40 px-3 py-2 mb-3 font-serif text-lg focus:outline-none focus:border-ink"
                value={s.heading}
                onChange={(e) => updateSection(i, "heading", e.target.value)}
              />
              <textarea
                rows={8}
                className="w-full border border-line bg-cream/40 px-3 py-2 text-sm leading-relaxed focus:outline-none focus:border-ink resize-y"
                value={s.body}
                onChange={(e) => updateSection(i, "body", e.target.value)}
              />
            </div>
          ))}
        </div>

        <button onClick={addSection} data-testid="add-section" className="mt-6 btn-outline-ink px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2">
          <Plus size={14} /> Add section
        </button>

        <div className="mt-10 flex items-center gap-4">
          <button
            onClick={save}
            disabled={saving}
            data-testid="edit-save"
            className="btn-terracotta px-6 py-3 rounded-full text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save changes"}
          </button>
          {saved && <span className="text-sm text-sage" data-testid="edit-saved">Saved.</span>}
        </div>
      </div>
    </section>
  );
};
